import { goClient } from '../utils/httpClientTs';

export interface LoanSummary {
  id: string;
  user_id: string;
  amount: number;
  status: string;
  current_principal_balance: number;
  current_interest_balance: number;
  current_deferred_balance: number;
  current_charges_balance: number;
  next_payment_date?: string;
  first_payment_date: string;
  start_date?: string;
  anniversary_date?: string;
  maturity_date?: string;
  original_payment_amount?: number;
  interest_rate: number; // percent points, e.g. 2.54 for 2.54%
  currency: string;
}

export interface BalanceLogEntry {
  /** YYYY-MM-DD calendar date */
  log_date: string;
  principal_balance: number;
  interest_balance: number;
  charges_balance: number;
  new_principal_balance: number;
  new_interest_balance: number;
  new_charges_balance: number;
  payment_allocation_id?: string;
  charge_id?: string;
  reason_id?: string;
  deferred_interest_log_id?: string;
}

export interface LoanDetailResponse extends LoanSummary {
  term_months: number;
  loan_type: string;
  balance_last_updated?: string;
  principal_only: number;
  auto_payment: number;
  loan_number?: string;
  local_chapter_id?: string;
  balance_logs: BalanceLogEntry[];
}

export interface CreateLoanRequest {
  user_id: string;
  amount: number;
  interest_rate: number; // percent points, e.g. 2.54 for 2.54%
  term_months: number;
  loan_type: string;
  first_payment_date: string;
  currency: string;
  status?: string;
  principal_only?: number;
  auto_payment?: number;
  local_chapter_id?: string;
  loan_number?: string;
  start_date?: string;
  anniversary_date?: string;
}

export interface ChargeTypeItem {
  id: string;
  name: string;
  amount: number;
}

export const getLoans = (params?: {
  user_id?: string;
}): Promise<LoanSummary[]> => {
  const query = params?.user_id
    ? `?user_id=${encodeURIComponent(params.user_id)}`
    : '';
  return goClient.get(`/loans${query}`).then((r) => r.data);
};

export const getChargeTypes = (): Promise<ChargeTypeItem[]> =>
  goClient.get('/loans/charge-types').then((r) => r.data);

export const getLoanDetail = (id: string): Promise<LoanDetailResponse> =>
  goClient.get(`/loans/${id}`).then((r) => r.data);

export const createLoan = (body: CreateLoanRequest): Promise<{ id: string }> =>
  goClient.post('/loans', body).then((r) => r.data);

export const addCharge = (
  loanId: string,
  body: { name: string; amount: number; start_date: string; end_date?: string }
): Promise<{ status: string }> =>
  goClient.post(`/loans/${loanId}/charges`, body).then((r) => r.data);

export const setLoanPrincipalOnly = (
  loanId: string,
  principalOnly: boolean
): Promise<LoanSummary> =>
  goClient
    .patch(`/loans/${loanId}/principal-only`, { principal_only: principalOnly })
    .then((r) => r.data);

export interface LoanPayoffQuote {
  loan_id: string;
  as_of: string;
  currency: string;
  principal: number;
  interest: number;
  charges: number;
  accrued_unbilled: number;
  total: number;
}

export interface LoanCatchUpQuote {
  loan_id: string;
  as_of: string;
  currency: string;
  catch_up_amount: number;
  eligible: boolean;
  behind: boolean;
  months_remaining: number;
  required_monthly: number;
  original_payment_amount: number;
}

export const getLoanPayoffQuote = (
  loanId: string,
  asOf?: string
): Promise<LoanPayoffQuote> => {
  const params = asOf ? `?as_of=${encodeURIComponent(asOf)}` : '';
  return goClient.get(`/loans/${loanId}/payoff${params}`).then((r) => r.data);
};

export const getLoanCatchUpQuote = (
  loanId: string
): Promise<LoanCatchUpQuote> =>
  goClient.get(`/loans/${loanId}/catch-up`).then((r) => r.data);

export const applyPayment = (body: {
  amount: number;
  posting_date: string;
  payment_method: string;
  payment_ref?: string;
  user_id?: string;
}): Promise<{ payment_id: string }> =>
  goClient.post('/loans/payments', body).then((r) => r.data);

export interface LoanCardPaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
}

export const createLoanCardPaymentIntent = (body: {
  user_id: string;
  amount: number;
}): Promise<LoanCardPaymentIntentResponse> =>
  goClient.post('/loans/payments/card/intent', body).then((r) => r.data);

export const confirmLoanCardPayment = (body: {
  user_id: string;
  payment_intent_id: string;
  posting_date?: string;
}): Promise<{ payment_id: string }> =>
  goClient.post('/loans/payments/card/confirm', body).then((r) => r.data);

export interface BackdatePaymentRequest {
  user_id: string;
  amount: number;
  posting_date: string;
  payment_method: string;
  payment_ref?: string;
}

export interface BackdatePaymentResponse {
  payment_id: string;
  posting_date: string;
  deleted_payment_ids?: string[];
  replayed_payment_ids?: string[];
  reapplied_charge_ids?: string[];
}

export const applyBackdatedPayment = (
  body: BackdatePaymentRequest
): Promise<BackdatePaymentResponse> =>
  goClient.post('/loans/payments/backdate', body).then((r) => r.data);

export interface LoanReplayRequest {
  source_loan_id: string;
  as_of_date?: string;
  tolerance_cents?: number;
}

export interface LoanReplayEnqueueResponse {
  task_log_id: string;
  task_id: string;
}

export interface BalanceSnapshot {
  principal: number;
  interest: number;
  deferred: number;
  charges: number;
}

export interface FieldCompare {
  source: number;
  replica: number;
  diff: number;
  match: boolean;
}

export interface BalanceCompare {
  principal: FieldCompare;
  interest: FieldCompare;
  deferred: FieldCompare;
  charges: FieldCompare;
}

export interface ReplayStep {
  index: number;
  event_date: string;
  event_type: string;
  detail: string;
  replica: BalanceSnapshot;
  source: BalanceSnapshot;
  step_match: boolean;
}

export interface LoanReplayReport {
  source_loan_id: string;
  replica_loan_id: string;
  replica_user_id: string;
  as_of_date: string;
  tolerance_cents: number;
  passed: boolean;
  final: BalanceCompare;
  steps: ReplayStep[];
  first_mismatch?: ReplayStep;
  error?: string;
}

export interface LoanReplayReportStatus {
  status: string;
  report?: LoanReplayReport;
  message?: string;
}

export const startLoanReplay = (
  body: LoanReplayRequest
): Promise<LoanReplayEnqueueResponse> =>
  goClient.post('/loans/replay', body).then((r) => r.data);

export const getLoanReplayReport = (
  taskLogId: string
): Promise<LoanReplayReportStatus> =>
  goClient.get(`/loans/replay/${taskLogId}`).then((r) => r.data);
